import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getUserDetailAction, clearUserDetailAction } from '../redux/actions/userDetailActions';

/**
 * Unified hook to manage user detail data fetching
 * Fetches data once and provides it to all tabs
 * Clears and refetches when userId changes to ensure fresh data
 */
export const useUserDetailData = (userId) => {
  const dispatch = useDispatch();
  const { userDetail, loading, error, accessDenied, accessReason, accessMessage } = useSelector((state) => state.userDetail);
  const previousUserIdRef = useRef(null);

  // Clear and fetch data when userId changes
  useEffect(() => {
    // If userId is null/undefined, clear data and reset ref
    if (!userId) {
      if (previousUserIdRef.current !== null) {
        dispatch(clearUserDetailAction());
        previousUserIdRef.current = null;
      }
      return;
    }

    // If userId changed, clear old data and fetch new data
    if (previousUserIdRef.current !== userId) {
      // Clear previous user's data
      dispatch(clearUserDetailAction());
      // Fetch new user's data
      dispatch(getUserDetailAction(userId));
      // Update the ref to track current userId
      previousUserIdRef.current = userId;
    } else if (!userDetail && previousUserIdRef.current === userId) {
      // If userId hasn't changed but data is missing, fetch it
      dispatch(getUserDetailAction(userId));
    }
  }, [dispatch, userId, userDetail]);

  const refetch = () => {
    if (userId) {
      dispatch(getUserDetailAction(userId));
    }
  };

  return {
    userDetail,
    loading,
    error,
    refetch,
    accessDenied,
    accessReason,
    accessMessage,
    // Provide specific data for each tab
    skillData: userDetail?.skillData || null,
    employmentData: userDetail?.employmentData || null,
    learningsData: userDetail?.learningsData || null,
    othersData: userDetail?.othersData || null
  };
};

